import React, { useCallback, useEffect, useState } from "react";
import { api, API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, DataTable } from "@/components/shared";
import { Card } from "@/components/agent/AgentPanels";
import { RecoveryCodes } from "@/components/MfaStep";
import { ChangeOwnPassword } from "@/components/PasswordForms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Lock, DatabaseBackup, Download, ShieldCheck, Mail } from "lucide-react";

function TwoStep() {
  const { user } = useAuth();
  const [on, setOn] = useState(!!user?.totp_enabled);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const admin = ["admin", "super_admin"].includes(user?.role);
  const start = async () => { try { const { data } = await api.post("/auth/mfa/enroll"); setSetup(data); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } };
  const confirm = async () => {
    try { const { data } = await api.post("/auth/mfa/enroll/confirm", { code }); setCodes(data.recovery_codes); setOn(true); setSetup(null); toast.success("2-step login is on"); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  const disable = async () => {
    const c = window.prompt("Enter a code from your authenticator app to turn 2-step login off");
    if (!c) return;
    try { await api.post("/auth/mfa/disable", { code: c }); setOn(false); toast.success("2-step login turned off"); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  if (codes) return <Card title="2-step login"><RecoveryCodes codes={codes} onContinue={() => setCodes(null)} /></Card>;
  return (
    <Card title="2-step login (authenticator app)">
      <p className="text-sm" data-testid="mfa-status">{on ? "On. You'll be asked for a 6-digit code at every sign-in." : "Off."} {admin && "Required for admins."}</p>
      <p className="text-xs text-muted-foreground mt-1">You are signed out automatically after 30 minutes of inactivity.</p>
      {!on && !setup && <Button size="sm" className="mt-3" onClick={start} data-testid="mfa-enroll-btn"><ShieldCheck className="h-4 w-4 mr-1" />Turn on</Button>}
      {on && !admin && <Button size="sm" variant="outline" className="mt-3" onClick={disable} data-testid="mfa-disable-btn">Turn off</Button>}
      {setup && (
        <div className="mt-3 flex flex-wrap gap-4 items-center">
          <img src={setup.qr} alt="QR" className="h-36 w-36 rounded border border-border bg-white p-1" />
          <div className="space-y-2"><p className="text-xs font-mono break-all max-w-xs" data-testid="mfa-enroll-secret">{setup.secret}</p>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="w-40" data-testid="mfa-enroll-code" />
            <Button size="sm" onClick={confirm} data-testid="mfa-enroll-confirm">Confirm</Button></div>
        </div>
      )}
    </Card>
  );
}

function History() {
  const { hasPerm } = useAuth();
  const [all, setAll] = useState(false);
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/auth/login-history", { params: { all_users: all } }).then(({ data }) => setRows(data.slice(0, 50))); }, [all]);
  const cols = [
    { key: "at", header: "When", render: (r) => fmtDateTime(r.at) },
    { key: "employee_id", header: "User", render: (r) => <span>{r.name || "—"} <span className="font-mono text-xs text-muted-foreground">{r.employee_id}</span></span> },
    { key: "success", header: "Result", render: (r) => <span className={r.success ? "text-emerald-600 text-sm font-semibold" : "text-rose-600 text-sm font-semibold"}>{r.reason.replace(/_/g, " ")}</span> },
    { key: "ip", header: "IP", render: (r) => <span className="font-mono text-xs">{r.ip}</span> },
    { key: "user_agent", header: "Device", render: (r) => <span className="text-xs text-muted-foreground line-clamp-1 max-w-xs">{r.user_agent}</span> },
  ];
  return (
    <Card title="Login history" actions={hasPerm("audit:view") && <label className="flex items-center gap-2 text-sm"><Switch checked={all} onCheckedChange={setAll} data-testid="history-all-switch" />All users</label>}>
      <DataTable columns={cols} rows={rows} rowTestId="login-history-row" emptyText="No sign-ins yet" />
    </Card>
  );
}

function Backups() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/backups").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); setTimeout(load, 1500); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } };
  const cols = [
    { key: "started_at", header: "Started", render: (b) => <span>{fmtDateTime(b.started_at)}<div className="text-[11px] text-muted-foreground">{b.trigger} · {b.by}</div></span> },
    { key: "status", header: "Status", render: (b) => <span className={b.status === "completed" ? "text-emerald-600 font-semibold" : b.status === "failed" ? "text-rose-600 font-semibold" : ""} data-testid="backup-status">{b.status}{b.error ? `: ${b.error}` : ""}</span> },
    { key: "total_docs", header: "Records", render: (b) => b.total_docs ?? "—" },
    { key: "size_bytes", header: "Size", render: (b) => (b.size_bytes ? `${(b.size_bytes / 1024).toFixed(0)} KB` : "—") },
    { key: "rc", header: "Restore check", render: (b) => { const c = (b.restore_checks || []).slice(-1)[0]; return c ? <span className={c.ok ? "text-emerald-600 text-sm font-semibold" : "text-rose-600 text-sm font-semibold"} data-testid="restore-check-result">{c.ok ? `Passed · ${c.docs_restored} records` : `Failed ${c.error || c.mismatches?.join("; ")}`}<div className="text-[11px] text-muted-foreground font-normal">{fmtDateTime(c.at)}</div></span> : "Not checked"; } },
    { key: "a", header: "", render: (b) => b.status === "completed" && <div className="flex gap-1 justify-end">
      <Button size="sm" variant="outline" className="h-7" onClick={() => act(() => api.post(`/admin/backups/${b.id}/restore-check`), "Restore check started")} data-testid="restore-check-btn">Restore check</Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => window.open(`${API}/admin/backups/${b.id}/download`, "_blank")} data-testid="backup-download-btn"><Download className="h-4 w-4" /></Button></div> },
  ];
  return (
    <Card title="Backups" actions={<>
      <Button size="sm" variant="outline" onClick={() => act(() => api.post("/admin/leaderboard/send-now"), "Leaderboard emails queued")} data-testid="leaderboard-send-btn"><Mail className="h-4 w-4 mr-1" />Send weekly leaderboard now</Button>
      <Button size="sm" onClick={() => act(() => api.post("/admin/backups"), "Backup started")} data-testid="backup-now-btn"><DatabaseBackup className="h-4 w-4 mr-1" />Back up now</Button></>}>
      <p className="text-xs text-muted-foreground mb-2">Automatic backup every day at 2:00 AM IST to secure storage. "Restore check" restores into a separate temporary copy and compares every record count. Live data is never touched.</p>
      <DataTable columns={cols} rows={rows} rowTestId="backup-row" emptyText="No backups yet" />
    </Card>
  );
}

function GoLive() {
  const [confirm, setConfirm] = useState("");
  const [staff, setStaff] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const loadPreview = () => api.get("/admin/go-live-cleanup/preview").then(({ data }) => setPreview(data)).catch((ex) => toast.error(apiError(ex.response?.data?.detail)));
  useEffect(() => { loadPreview(); }, []);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/go-live-cleanup", { confirm, remove_staff: staff }); setRes(data); setConfirm(""); loadPreview(); toast.success("Demo data removed. A backup was saved first."); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border-2 border-rose-200 bg-rose-50/40 p-5" data-testid="golive-danger-zone">
      <div className="font-bold text-rose-700 mb-1">Danger zone · Go-live cleanup</div>
      <p className="text-sm text-muted-foreground">Takes a full backup, then deletes all leads, customers, policies, loans, agents, calls and other records. Settings, products, lenders, insurers, form builder, rules, real staff and the audit log are kept.</p>
      {preview && <div className="text-sm mt-2" data-testid="cleanup-preview">Will delete <b>{preview.total}</b> records{preview.test_products ? ` and ${preview.test_products} test products` : ""}.
        <span className="text-xs text-muted-foreground block">{Object.entries(preview.records).map(([k, v]) => `${k} ${v}`).join(" · ")}</span></div>}
      <label className="flex items-center gap-2 text-sm mt-2"><Switch checked={staff} onCheckedChange={setStaff} data-testid="cleanup-staff-switch" />Also remove the {preview?.demo_staff?.length ?? 0} demo staff logins{preview?.demo_staff?.length ? ` (${preview.demo_staff.map((s) => s.employee_id).join(", ")})` : ""}</label>
      <div className="flex gap-2 mt-3 flex-wrap">
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type DELETE DEMO DATA" className="w-64 bg-white" data-testid="cleanup-confirm-input" />
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="destructive" disabled={busy || confirm !== "DELETE DEMO DATA"} data-testid="cleanup-run-btn">Delete demo data</Button></AlertDialogTrigger>
          <AlertDialogContent data-testid="cleanup-final-dialog">
            <AlertDialogHeader><AlertDialogTitle>Delete {preview?.total ?? 0} records for good?</AlertDialogTitle>
              <AlertDialogDescription>A full backup is taken first. {staff ? `${preview?.demo_staff?.length ?? 0} demo staff logins will also be removed.` : "All staff logins are kept."} This cannot be undone from this screen.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="cleanup-cancel-btn">Cancel</AlertDialogCancel><AlertDialogAction onClick={go} className="bg-rose-600 hover:bg-rose-700" data-testid="cleanup-final-confirm-btn">Yes, delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {res && <p className="text-sm mt-2" data-testid="cleanup-result">Backup saved ({res.backup_records} records). Deleted {Object.values(res.deleted).reduce((a, b) => a + b, 0)} records{res.removed_staff ? ` and ${res.removed_staff} staff logins` : ""}.</p>}
    </div>
  );
}

export default function Security() {
  const { hasPerm, user } = useAuth();
  return (
    <div className="space-y-4">
      <PageHeader title="Security" subtitle="2-step login, sign-in history and backups" icon={Lock} />
      <TwoStep />
      <Card title="Change my password"><ChangeOwnPassword /></Card>
      <History />
      {hasPerm("*") && <Backups />}
      {user?.role === "super_admin" && <GoLive />}
    </div>
  );
}
